"use client";

/* eslint-disable react/only-export-components -- co-located helper. */

import * as React from "react";
import { Check, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import type { Citation } from "@quovy/field-types";
import { Button } from "../../inputs/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../../primitives/hover-card";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../primitives/tooltip";
import { cn } from "../../utils";
import { CitationInfoCard } from "./citation-info-card";
import {
  PDFPreview as PDFPreviewShared,
  TextFilePreview as TextFilePreviewShared,
  TextBasedPreview as TextBasedPreviewShared,
  EmailPreview as EmailPreviewShared,
  SpreadsheetPreview as SpreadsheetPreviewShared,
  URLPreview as URLPreviewShared,
  HtmlPreview as HtmlPreviewShared,
  PreviewLoading,
} from "./previews";
import type {
  CitationBoundingBox,
  CitationFileType,
  CitationHoverData,
  CitationSource,
} from "./types";

export type { CitationBoundingBox, CitationFileType, CitationHoverData, CitationSource };

/**
 * Detect the file type category from MIME type or filename.
 * When sourceUrl is set and there is no fileUrl, returns "url".
 */
export function detectFileType(
  mimeType?: string,
  filename?: string,
  options?: { sourceUrl?: string; fileUrl?: string },
): CitationFileType {
  if (options?.sourceUrl && !options.fileUrl) return "url";

  if (mimeType) {
    if (mimeType === "application/pdf") return "pdf";
    if (mimeType.includes("spreadsheet") || mimeType.includes("excel") || mimeType === "text/csv")
      return "spreadsheet";
    if (mimeType === "text/html") return "html";
    if (mimeType.startsWith("image/")) return "image";
    if (mimeType === "message/rfc822" || mimeType === "text/x-eml") return "email";
    if (mimeType.startsWith("text/")) return "text";
  }

  if (filename) {
    const ext = filename.toLowerCase().split(".").pop();
    switch (ext) {
      case "pdf":
        return "pdf";
      case "csv":
      case "xlsx":
      case "xls":
      case "ods":
        return "spreadsheet";
      case "html":
      case "htm":
        return "html";
      case "eml":
      case "msg":
        return "email";
      case "txt":
      case "md":
      case "mdc":
      case "log":
      case "json":
      case "xml":
        return "text";
      case "png":
      case "jpg":
      case "jpeg":
      case "gif":
      case "webp":
        return "image";
    }
  }

  return "unknown";
}

// ============ Adapters: wrap shared preview components with CitationHoverData ============
//
// Each adapter below is pure prop plumbing: pull the fields the shared
// preview needs off `CitationHoverData` and forward them. `createPreviewAdapter`
// collapses that shape into a single factory; only the field mapping differs
// per file type.

function createPreviewAdapter<P extends object>(
  Shared: React.ComponentType<P>,
  toProps: (citation: CitationHoverData) => P,
  displayName: string,
): React.FC<{ citation: CitationHoverData }> {
  function PreviewAdapter({ citation }: { citation: CitationHoverData }) {
    return <Shared {...toProps(citation)} />;
  }
  PreviewAdapter.displayName = displayName;
  return PreviewAdapter;
}

const PDFPreview = createPreviewAdapter(
  PDFPreviewShared,
  (citation) => ({
    fileUrl: citation.fileUrl,
    filename: citation.filename,
    id: citation.id,
    fieldKey: citation.fieldKey,
    value: citation.value,
    citedText: citation.citedText,
    pageNumber: citation.pageNumber,
    boundingBox: citation.boundingBox,
    isConflict: citation.isConflict,
  }),
  "PDFPreview",
);

const TextPreview = createPreviewAdapter(
  TextFilePreviewShared,
  (citation) => ({
    fileUrl: citation.fileUrl,
    filename: citation.filename,
    citedText: citation.citedText,
  }),
  "TextPreview",
);

const EmailPreview = createPreviewAdapter(
  EmailPreviewShared,
  (citation) => ({
    filename: citation.filename,
    citedText: citation.citedText,
  }),
  "EmailPreview",
);

const SpreadsheetPreview = createPreviewAdapter(
  SpreadsheetPreviewShared,
  (citation) => ({
    filename: citation.filename,
    citedText: citation.citedText,
    pageNumber: citation.pageNumber,
  }),
  "SpreadsheetPreview",
);

const HtmlPreview = createPreviewAdapter(
  HtmlPreviewShared,
  (citation) => ({
    fileUrl: citation.fileUrl,
    filename: citation.filename,
    citedText: citation.citedText,
  }),
  "HtmlPreview",
);

const URLPreview = createPreviewAdapter(
  URLPreviewShared,
  (citation) => ({
    sourceUrl: citation.sourceUrl ?? "",
    citedText: citation.citedText,
  }),
  "URLPreview",
);

// TextBasedPreview also takes a `fileType` derived by the dispatch site, so
// it doesn't fit the single-arg `createPreviewAdapter` shape.
function TextBasedPreview({
  citation,
  fileType,
}: {
  citation: CitationHoverData;
  fileType: CitationFileType;
}) {
  return (
    <TextBasedPreviewShared
      filename={citation.filename}
      citedText={citation.citedText}
      pageNumber={citation.pageNumber}
      fileType={fileType}
    />
  );
}

// ============ Citation Hover Card Header ============

/** Prev/next paging controls shown when a hover card carries >1 citation. */
interface CitationHoverNav {
  /** Zero-based index of the citation currently shown. */
  index: number;
  /** Total number of citations in the set. */
  total: number;
  onPrev: () => void;
  onNext: () => void;
}

interface CitationHoverHeaderProps {
  citation: CitationHoverData;
  onOpenInDrawer?: (citation: CitationHoverData) => void;
  /** When set, renders ‹ n / total › paging controls. */
  nav?: CitationHoverNav;
  /** Marks the shown citation as the one whose value the field adopted. */
  showUsedBadge?: boolean;
  className?: string;
}

function CitationHoverHeader({
  citation,
  onOpenInDrawer,
  nav,
  showUsedBadge,
  className,
}: CitationHoverHeaderProps) {
  const canonical: Citation = {
    id: citation.id,
    document_id: "",
    label: citation.filename,
    ...(citation.pageNumber != null ? { page: citation.pageNumber } : {}),
    ...(citation.citedText ? { snippet: citation.citedText } : {}),
  };

  return (
    <div
      className={cn(
        "px-3 py-2 bg-gradient-to-r from-stone-50 to-stone-100 dark:from-stone-800 dark:to-stone-750 border-b border-stone-200 dark:border-stone-700",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {(citation.fieldLabel || citation.fieldKey) && (
              <span className="text-[10px] font-medium text-stone-500 dark:text-stone-400 uppercase tracking-wide">
                {citation.fieldLabel || formatFieldKey(citation.fieldKey || "")}
              </span>
            )}
            {showUsedBadge && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center justify-center rounded-full bg-emerald-100 p-0.5 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                    <Check className="h-2.5 w-2.5" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>This citation was used for the value.</TooltipContent>
              </Tooltip>
            )}
          </div>
          <CitationInfoCard
            citation={canonical}
            value={citation.value ?? ""}
            filename={citation.filename}
            {...(citation.pageNumber != null ? { pageNumber: citation.pageNumber } : {})}
            citedText={citation.citedText}
            confidence={citation.confidence ?? 0}
            {...(citation.isConflict != null ? { isConflict: citation.isConflict } : {})}
            {...(citation.isSourceOfTruth != null
              ? { isSourceOfTruth: citation.isSourceOfTruth }
              : {})}
            {...(citation.isSuggested != null ? { isSuggested: citation.isSuggested } : {})}
            {...(citation.sourceUrl != null ? { sourceUrl: citation.sourceUrl } : {})}
          />
        </div>

        <div className="flex flex-shrink-0 items-center gap-0.5 pt-0.5">
          {nav && (
            <div className="flex items-center gap-0.5">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  nav.onPrev();
                }}
                className={cn(
                  "size-6 rounded",
                  "text-stone-400 hover:bg-stone-200/50 hover:text-stone-600",
                  "dark:text-stone-500 dark:hover:bg-stone-700/50 dark:hover:text-stone-300",
                )}
                aria-label="Previous citation"
                title="Previous citation"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <span className="min-w-[2.5rem] text-center text-[10px] tabular-nums text-stone-500 dark:text-stone-400">
                {nav.index + 1} / {nav.total}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  nav.onNext();
                }}
                className={cn(
                  "size-6 rounded",
                  "text-stone-400 hover:bg-stone-200/50 hover:text-stone-600",
                  "dark:text-stone-500 dark:hover:bg-stone-700/50 dark:hover:text-stone-300",
                )}
                aria-label="Next citation"
                title="Next citation"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
          {onOpenInDrawer && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onOpenInDrawer(citation);
              }}
              className={cn(
                "-mr-1 size-6 rounded",
                "text-stone-400 hover:bg-stone-200/50 hover:text-stone-600",
                "dark:text-stone-500 dark:hover:bg-stone-700/50 dark:hover:text-stone-300",
              )}
              title="Open in drawer"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ Citation Hover Card Content Preview ============

interface CitationHoverPreviewProps {
  citation: CitationHoverData;
  renderPreview?: (citation: CitationHoverData) => React.ReactNode;
  showFilePreview?: boolean;
  className?: string;
}

function CitationHoverPreview({
  citation,
  renderPreview,
  showFilePreview = true,
  className,
}: CitationHoverPreviewProps) {
  const fileType = detectFileType(citation.fileType, citation.filename, {
    ...(citation.sourceUrl != null ? { sourceUrl: citation.sourceUrl } : {}),
    ...(citation.fileUrl != null ? { fileUrl: citation.fileUrl } : {}),
  });

  function renderContent(): React.ReactNode {
    if (renderPreview) {
      return renderPreview(citation);
    }

    if (fileType === "url" && citation.sourceUrl) {
      return <URLPreview citation={citation} />;
    }

    if (showFilePreview && citation.fileUrl) {
      switch (fileType) {
        case "pdf":
          return <PDFPreview citation={citation} />;
        case "text":
          return <TextPreview citation={citation} />;
        case "html":
          return <HtmlPreview citation={citation} />;
        case "email":
          return <EmailPreview citation={citation} />;
        case "spreadsheet":
          return <SpreadsheetPreview citation={citation} />;
      }
    }

    return <TextBasedPreview citation={citation} fileType={fileType} />;
  }

  return <div className={cn("flex-1 overflow-hidden", className)}>{renderContent()}</div>;
}

// ============ Citation Hover Card ============

export interface CitationHoverCardProps {
  /**
   * Single citation to display. Convenience prop for the common one-citation
   * case. When `citations` is also provided, `citations` wins.
   */
  citation?: CitationHoverData;
  /**
   * Citation set to page through. When more than one is supplied the header
   * shows ‹ n / total › controls so the operator can loop through every
   * citation backing the value. Prefer this over `citation` for multi-source
   * fields.
   */
  citations?: CitationHoverData[];
  /**
   * Id of the citation whose value the field actually adopted. The matching
   * citation is flagged with a "Used for value" badge while paging.
   */
  usedCitationId?: string;
  /** Citation to show first when the card opens. Defaults to `usedCitationId`, then the first. */
  initialCitationId?: string;
  /** Children element that triggers the hover */
  children: React.ReactNode;
  /** Custom content renderer for the preview area */
  renderPreview?: (citation: CitationHoverData) => React.ReactNode;
  /**
   * Callback when "Open in drawer" is clicked. When provided, a Maximize
   * button appears in the header. Quovy4 has no global drawer wired up
   * yet, so callers can decide what to do (e.g. open `DocumentPreview`).
   */
  onOpenInDrawer?: (citation: CitationHoverData) => void;
  /** Delay before opening (ms) */
  openDelay?: number;
  /** Delay before closing (ms) */
  closeDelay?: number;
  /** Side to render the card */
  side?: "top" | "right" | "bottom" | "left";
  /** Alignment of the card */
  align?: "start" | "center" | "end";
  /** Additional class name for the content */
  className?: string;
  /** Width of the hover card */
  width?: "sm" | "md" | "lg" | "xl" | "full";
}

/**
 * [WIP] Citation Hover Card
 *
 * A hover card component for displaying citation details on hover.
 * Shows field info, source metadata, and cited text excerpt.
 *
 * Pass a custom `renderPreview` to integrate with file viewers (PDF, etc).
 */
export function CitationHoverCard({
  citation,
  citations,
  usedCitationId,
  initialCitationId,
  children,
  renderPreview,
  onOpenInDrawer,
  openDelay = 500,
  closeDelay = 100,
  side = "top",
  align = "center",
  className,
  width = "md",
}: CitationHoverCardProps) {
  const widthClasses: Record<string, string> = {
    sm: "w-64",
    md: "w-80",
    lg: "w-96",
    xl: "w-[480px]",
    full: "w-[calc(100vw-2rem)] max-w-[600px]",
  };

  const list = React.useMemo<CitationHoverData[]>(
    () => (citations && citations.length > 0 ? citations : citation ? [citation] : []),
    [citations, citation],
  );

  if (list.length === 0) return <>{children}</>;

  return (
    <HoverCard openDelay={openDelay} closeDelay={closeDelay}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        className={cn("p-0 overflow-hidden", widthClasses[width], className)}
        side={side}
        align={align}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Keyed/remounted with the content each open, so paging resets to the
            used citation every time the card reopens. */}
        <CitationHoverBody
          list={list}
          {...(usedCitationId ? { usedCitationId } : {})}
          {...(initialCitationId ? { initialCitationId } : {})}
          {...(renderPreview ? { renderPreview } : {})}
          {...(onOpenInDrawer ? { onOpenInDrawer } : {})}
        />
      </HoverCardContent>
    </HoverCard>
  );
}

// ============ Citation Hover Card Body (paging) ============

interface CitationHoverBodyProps {
  list: CitationHoverData[];
  usedCitationId?: string;
  initialCitationId?: string;
  renderPreview?: (citation: CitationHoverData) => React.ReactNode;
  onOpenInDrawer?: (citation: CitationHoverData) => void;
}

/**
 * Holds the paging index for a multi-citation hover card. Lives inside
 * `HoverCardContent` so Radix remounts it (and resets the index) every time
 * the card opens.
 */
function CitationHoverBody({
  list,
  usedCitationId,
  initialCitationId,
  renderPreview,
  onOpenInDrawer,
}: CitationHoverBodyProps) {
  const total = list.length;
  const initialIndex = React.useMemo(() => {
    const target = initialCitationId ?? usedCitationId;
    const i = target ? list.findIndex((c) => c.id === target) : -1;
    return i >= 0 ? i : 0;
  }, [list, initialCitationId, usedCitationId]);
  const [index, setIndex] = React.useState(initialIndex);

  const safeIndex = Math.min(index, total - 1);
  const current = list[safeIndex];
  if (!current) return null;

  const go = (delta: number) =>
    setIndex((prev) => {
      const base = Math.min(prev, total - 1);
      return (base + delta + total) % total;
    });

  const isUsed = usedCitationId != null && current.id === usedCitationId;
  const nav: CitationHoverNav | undefined =
    total > 1 ? { index: safeIndex, total, onPrev: () => go(-1), onNext: () => go(1) } : undefined;

  return (
    <>
      <CitationHoverHeader
        citation={current}
        {...(onOpenInDrawer ? { onOpenInDrawer } : {})}
        {...(nav ? { nav } : {})}
        showUsedBadge={isUsed && total > 1}
      />
      <CitationHoverPreview citation={current} {...(renderPreview ? { renderPreview } : {})} />
    </>
  );
}

// ============ Helpers ============

/**
 * Format a field key for display (e.g., "named_insured" -> "Named Insured")
 */
function formatFieldKey(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Convert a generic citation source to CitationHoverData.
 * Use this to avoid duplicating conversion logic across components.
 */
export function toCitationHoverData(
  citation: CitationSource,
  options?: {
    fieldKey?: string;
    fieldLabel?: string;
  },
): CitationHoverData {
  return {
    id: citation.id,
    ...(citation.value != null ? { value: citation.value } : {}),
    ...(options?.fieldKey != null ? { fieldKey: options.fieldKey } : {}),
    ...(options?.fieldLabel != null ? { fieldLabel: options.fieldLabel } : {}),
    filename: citation.filename,
    ...(citation.fileUrl != null ? { fileUrl: citation.fileUrl } : {}),
    ...(citation.fileType != null ? { fileType: citation.fileType } : {}),
    ...(citation.pageNumber != null ? { pageNumber: citation.pageNumber } : {}),
    citedText: citation.citedText,
    ...(citation.confidence != null ? { confidence: citation.confidence } : {}),
    ...(citation.isConflict != null ? { isConflict: citation.isConflict } : {}),
    ...(citation.isSuggested != null ? { isSuggested: citation.isSuggested } : {}),
    ...(citation.isSourceOfTruth != null ? { isSourceOfTruth: citation.isSourceOfTruth } : {}),
    ...(citation.boundingBox != null ? { boundingBox: citation.boundingBox } : {}),
    ...(citation.sourceUrl != null ? { sourceUrl: citation.sourceUrl } : {}),
  };
}

export {
  CitationHoverHeader,
  CitationHoverPreview,
  PDFPreview,
  TextPreview,
  HtmlPreview,
  EmailPreview,
  SpreadsheetPreview,
  URLPreview,
  TextBasedPreview,
  PreviewLoading,
};
